/**
 * Material Master — Server-side paginated catalog.
 *
 * Layout:
 *   left rail  : 3-level Group → Category tree with counts
 *   main area  : KPI strip + filter bar + paginated flat table + Add/Edit panel
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Boxes, Plus, Search, X, ChevronDown, ChevronRight,
  Edit2, Trash2, Tag, Hash, Loader2, Database, Sparkles, FileBox, Wrench, Copy,
} from 'lucide-react';
import { api, apiGet, apiPost } from '../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';

const UOM_OPTIONS = ['Nos', 'KG', 'MT', 'Bag', 'Bundle', 'Roll', 'M', 'M²', 'M³', 'Ltr', 'Cum', 'Sqft', 'Cft', 'Set', 'Pack', 'Drum', 'Tin', 'Box', 'Coil', 'Length'];
const PAGE_SIZE = 50;

export default function MaterialMaster() {
  // Pagination & data
  const [materials, setMaterials] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total_materials: 0, categories: 0, hsn_codes: 0, brands: 0 });

  // Left-rail groups
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hsnCodes, setHsnCodes] = useState([]);

  // Server-side filters
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterHSN, setFilterHSN] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterVariant, setFilterVariant] = useState('');
  const [brands, setBrands] = useState([]);

  // Panel
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [repairing, setRepairing] = useState(false);

  // VIEW MODE: grouped (base-item view) vs flat (legacy)
  const [viewMode, setViewMode] = useState('grouped');
  const [baseItems, setBaseItems] = useState([]);
  const [expandedBase, setExpandedBase] = useState({});     // base_item_name → variants[]
  const [loadingVariants, setLoadingVariants] = useState({}); // base_item_name → bool
  const [baseTotal, setBaseTotal] = useState(0);
  const [basePage, setBasePage] = useState(1);
  const [baseLoading, setBaseLoading] = useState(false);

  const hasFilter = !!(search || filterCategory || filterGroup || filterHSN || filterBrand || filterVariant);

  // ── Load paginated materials — ONLY when a filter/search/category is active ──
  // With 25,000+ SKUs, loading all on mount freezes the browser. Users must
  // select a category or enter a search term first.
  const load = useCallback(async () => {
    if (!hasFilter) {
      // No filter active — show empty state prompting user to select a category
      setMaterials([]);
      setTotalCount(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.q = search;
      if (filterCategory) params.category = filterCategory;
      if (filterGroup) params.group = filterGroup;
      if (filterHSN) params.hsn_code = filterHSN;
      if (filterBrand) params.brand = filterBrand;
      if (filterVariant) params.variant = filterVariant;
      const [mats, sum] = await Promise.all([
        apiGet('/material-master', params),
        apiGet('/material-master-stats/summary'),
      ]);
      setMaterials(mats?.items || []);
      setTotalCount(mats?.total || 0);
      setTotalPages(mats?.pages || 1);
      setSummary(sum || {});
    } catch {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterCategory, filterGroup, filterHSN, filterBrand, filterVariant, hasFilter]);

  // Load groups + HSN codes once
  useEffect(() => {
    apiGet('/material-master/distinct-groups').then((r) => {
      const items = r?.items || [];
      setGroups(items);
      // Expand all groups by default
      const init = {};
      items.forEach((g) => { init[g.group] = true; });
      setExpandedGroups(init);
    });
    apiGet('/material-master/hsn-codes').then((r) => setHsnCodes(r?.items || []));
  }, []);

  // Debounce search, immediate on other filter changes
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filterCategory, filterGroup, filterHSN, filterBrand, filterVariant]);

  // Fetch brands when category changes
  useEffect(() => {
    const params = filterCategory ? { category: filterCategory } : {};
    apiGet('/material-master/distinct-brands', params).then((r) => setBrands(r?.items || []));
  }, [filterCategory]);

  const clearFilters = () => {
    setSearch(''); setFilterCategory(''); setFilterGroup('');
    setFilterHSN(''); setFilterBrand(''); setFilterVariant(''); setPage(1);
  };

  const onSaved = () => { setPanelOpen(false); setEditing(null); load(); };
  const onEdit = (m) => { setEditing(m); setPanelOpen(true); };
  const onAdd = () => { setEditing(null); setPanelOpen(true); };
  const onDelete = async (m) => {
    if (!window.confirm(`Delete material "${m.name}" (${m.material_id})?`)) return;
    try {
      await api.delete(`/material-master/${m.id}`);
      toast.success('Material removed');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };
  const onSeedCatalog = async () => {
    if (!window.confirm('Load the curated RMC plant materials catalog?\nExisting items will not be duplicated.')) return;
    setSeeding(true);
    try {
      const { data } = await api.post('/material-master/seed-defaults');
      toast.success(`Catalog loaded · ${data.inserted} added, ${data.skipped_duplicates} skipped`);
      load();
      // Refresh groups after seed
      apiGet('/material-master/distinct-groups').then((r) => setGroups(r?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally { setSeeding(false); }
  };

  const onRepairCatalog = async () => {
    setRepairing(true);
    try {
      // Dry run first — show the user exactly what would change.
      const { data: dry } = await api.post('/material-master/admin/repair-catalog?dry_run=true');
      if (!dry.duplicates_found && !dry.missing_group) {
        toast.success('Catalog is healthy — no duplicates, no missing groups');
        return;
      }
      const msg = `Catalog repair will:\n` +
        `• Deactivate ${dry.duplicates_found} duplicate material(s) across ${dry.duplicate_groups} group(s)\n` +
        `• Backfill the group field on ${dry.missing_group} record(s)\n\n` +
        `Duplicates are deactivated (not deleted) so old PO references stay intact. Proceed?`;
      if (!window.confirm(msg)) return;
      const { data } = await api.post('/material-master/admin/repair-catalog?dry_run=false');
      toast.success(`Repaired · ${data.duplicates_deactivated} duplicates removed, ${data.groups_backfilled} groups backfilled`);
      load();
      apiGet('/material-master/distinct-groups').then((r) => setGroups(r?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally { setRepairing(false); }
  };

  const setGroupFilter = (grp) => {
    setFilterGroup(grp === filterGroup ? '' : grp);
    setFilterCategory('');
  };
  const setCategoryFilter = (grp, cat) => {
    setFilterGroup(grp);
    setFilterCategory(cat === filterCategory ? '' : cat);
  };

  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="space-y-4" data-testid="material-master-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">
            <Database size={11} /> Procurement · Master Data
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-900 mt-1 flex items-center gap-2">
            <FileBox size={22} className="text-[#1F7A6E]" /> Material Master
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Central catalog of every plant material — categorised by HSN code. No rates here; pricing lives in vendor quotes &amp; PO line items.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRepairCatalog}
            disabled={repairing}
            title="Fix duplicate materials & missing tree groups"
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="mm-repair-btn"
          >
            {repairing ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
            Repair Catalog
          </button>
          <button
            onClick={onSeedCatalog}
            disabled={seeding}
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50"
            data-testid="mm-seed-btn"
          >
            {seeding ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Load Catalog
          </button>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-bold uppercase tracking-wider"
            data-testid="mm-add-btn"
          >
            <Plus size={12} /> Add Material
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Boxes} label="Total Materials" value={summary.total_materials?.toLocaleString()} />
        <Kpi icon={Tag} label="Categories" value={summary.categories} />
        <Kpi icon={Hash} label="HSN Codes" value={summary.hsn_codes} />
        <Kpi icon={Database} label="Brands" value={summary.brands} />
      </div>

      {/* Body: Left Rail + Table */}
      <div className="flex gap-4 items-start">
        {/* ── Left Rail: Group Tree ── */}
        <aside className="w-56 shrink-0 bg-white border border-slate-200 rounded-sm p-2 sticky top-4 max-h-[80vh] overflow-y-auto" data-testid="mm-group-tree">
          <button
            onClick={clearFilters}
            className={`w-full text-left px-2 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider mb-1 ${!hasFilter ? 'bg-[#1F7A6E] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid="mm-filter-all"
          >
            All Materials
            <span className="ml-1 text-[10px] font-normal">({summary.total_materials?.toLocaleString() || 0})</span>
          </button>
          {groups.map((g) => (
            <GroupNode
              key={g.group}
              group={g}
              activeGroup={filterGroup}
              activeCategory={filterCategory}
              expanded={expandedGroups[g.group]}
              onToggle={() => setExpandedGroups((x) => ({ ...x, [g.group]: !x[g.group] }))}
              onGroupClick={() => setGroupFilter(g.group)}
              onCategoryClick={(cat) => setCategoryFilter(g.group, cat)}
            />
          ))}
        </aside>

        {/* ── Main Area ── */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Filter Bar */}
          <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap gap-2 items-center" data-testid="mm-filter-bar">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, brand, spec, HSN, Material ID…"
                className="w-full pl-8 pr-8 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="mm-search-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              )}
            </div>
            {/* HSN */}
            <input
              value={filterHSN}
              onChange={(e) => setFilterHSN(e.target.value)}
              placeholder="HSN code"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-28"
              data-testid="mm-filter-hsn"
            />
            {/* Brand */}
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
              data-testid="mm-filter-brand"
            >
              <option value="">All Brands</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            {/* Variant */}
            <input
              value={filterVariant}
              onChange={(e) => setFilterVariant(e.target.value)}
              placeholder="Variant / size"
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-32"
              data-testid="mm-filter-variant"
            />
            {hasFilter && (
              <button onClick={clearFilters} className="text-xs text-rose-600 hover:underline flex items-center gap-1" data-testid="mm-clear-filters">
                <X size={11} /> Clear
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
                <Loader2 size={18} className="animate-spin" /> Loading…
              </div>
            ) : materials.length === 0 ? (
              <div className="text-center py-16 text-slate-400 text-sm font-display">
                {!hasFilter ? (
                  <div className="space-y-2">
                    <div className="text-slate-500 font-semibold">Select a category from the left rail or search to load materials.</div>
                    <div className="text-xs text-slate-400">Category-filtered loading keeps the page fast as the catalog grows.</div>
                    <button onClick={onSeedCatalog} className="mt-2 text-[#1F7A6E] hover:underline font-bold text-xs uppercase tracking-wider">Load Catalog from Defaults</button>
                  </div>
                ) : (
                  <span className="uppercase tracking-widest text-xs">No materials found matching the current filter.</span>
                )}
              </div>
            ) : (
              <table className="w-full dense-table" data-testid="mm-table">
                <thead>
                  <tr>
                    <th className="w-28">Material ID</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Sub-Cat</th>
                    <th>Brand</th>
                    <th>Variant</th>
                    <th className="w-10 text-center">UoM</th>
                    <th className="w-14">HSN</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m) => (
                    <tr key={m.id} data-testid={`mm-row-${m.id}`} className="hover:bg-slate-50">
                      <td className="font-mono text-xs text-[#1F7A6E] font-bold whitespace-nowrap">{m.material_id}</td>
                      <td className="font-display font-semibold max-w-[220px]">
                        <div className="truncate">{m.name}</div>
                        {m.specification && <div className="text-[10px] text-slate-500 truncate">{m.specification}</div>}
                      </td>
                      <td className="text-xs">{m.category}</td>
                      <td className="text-xs text-slate-500">{m.sub_category || '—'}</td>
                      <td className="text-xs">{m.brand || '—'}</td>
                      <td className="text-xs text-slate-500">{m.variant || '—'}</td>
                      <td className="text-center text-xs font-mono">{m.uom}</td>
                      <td className="text-xs font-mono text-slate-500">{m.hsn_code}</td>
                      <td>
                        <div className="flex items-center gap-1.5 justify-end">
                          {m.is_critical && <span className="text-[9px] text-rose-600 font-bold uppercase" title="Critical">C</span>}
                          {m.is_consumable && <span className="text-[9px] text-amber-600 font-bold uppercase" title="Consumable">S</span>}
                          <button onClick={() => onEdit(m)} className="text-slate-400 hover:text-[#1F7A6E]" data-testid={`mm-edit-${m.id}`}><Edit2 size={12} /></button>
                          <button onClick={() => onDelete(m)} className="text-slate-400 hover:text-rose-600" data-testid={`mm-del-${m.id}`}><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-4 py-2" data-testid="mm-pagination">
              <span className="text-xs text-slate-500 font-display">
                Showing {start}–{end} of <strong>{totalCount.toLocaleString()}</strong> materials
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100 font-display"
                  data-testid="mm-prev-page"
                >
                  ← Prev
                </button>
                <span className="text-xs text-slate-600 font-display font-bold">Page {page} of {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100 font-display"
                  data-testid="mm-next-page"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Side Panel — unchanged */}
      <MaterialSidePanel
        open={panelOpen}
        onOpenChange={(o) => { setPanelOpen(o); if (!o) setEditing(null); }}
        material={editing}
        hsnCodes={hsnCodes}
        onSaved={onSaved}
      />
    </div>
  );
}

// ── Left-rail group node ────────────────────────────────────────────────────
function GroupNode({ group, activeGroup, activeCategory, expanded, onToggle, onGroupClick, onCategoryClick }) {
  const isActive = activeGroup === group.group && !activeCategory;
  return (
    <div className="mb-0.5">
      <div className="flex items-center gap-1">
        <button
          onClick={onToggle}
          className="text-slate-400 hover:text-slate-700 p-0.5"
        >
          {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </button>
        <button
          onClick={onGroupClick}
          className={`flex-1 text-left px-1.5 py-1 rounded-sm text-[11px] font-display font-bold truncate ${
            isActive ? 'bg-[#1F7A6E] text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
          data-testid={`mm-group-${group.group}`}
        >
          {group.group}
          <span className={`ml-1 font-normal ${isActive ? 'text-white/80' : 'text-slate-400'}`}>({group.total})</span>
        </button>
      </div>
      {expanded && (
        <div className="ml-4 mt-0.5 space-y-0.5">
          {group.categories.map((c) => {
            const catActive = activeGroup === group.group && activeCategory === c.category;
            return (
              <button
                key={c.category}
                onClick={() => onCategoryClick(c.category)}
                className={`w-full text-left px-2 py-1 rounded-sm text-[10px] font-display truncate ${
                  catActive ? 'bg-[#eef1f8] text-[#1F7A6E] font-bold' : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`mm-cat-${c.category}`}
              >
                {c.category}
                <span className="ml-1 text-slate-400">({c.count})</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── KPI chip ────────────────────────────────────────────────────────────────
function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</span>
        <Icon size={14} className="text-slate-400" />
      </div>
      <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">{value ?? 0}</div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Side Panel — Add / Edit (unchanged from original)
// ----------------------------------------------------------------------------

function MaterialSidePanel({ open, onOpenChange, material, hsnCodes, onSaved }) {
  const isEdit = !!material;
  const [form, setForm] = useState(blankForm());
  const [hsnQuery, setHsnQuery] = useState('');
  const [showHsnDrop, setShowHsnDrop] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(material ? { ...blankForm(), ...material, tags: material.tags || [] } : blankForm());
      setHsnQuery('');
      setShowHsnDrop(false);
      setTagInput('');
    }
  }, [open, material]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const filteredHsn = useMemo(() => {
    const s = hsnQuery.trim().toLowerCase();
    const list = hsnCodes || [];
    if (!s) return list.slice(0, 25);
    return list.filter((h) =>
      [h.hsn, h.category, h.description].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    ).slice(0, 25);
  }, [hsnCodes, hsnQuery]);

  const pickHsn = (h) => {
    set('hsn_code', h.hsn);
    if (!form.category) set('category', h.category);
    setHsnQuery(`${h.hsn} — ${h.category}`);
    setShowHsnDrop(false);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set('tags', [...form.tags, t]);
    setTagInput('');
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Name is required');
    if (!form.hsn_code) return toast.error('HSN code is required');
    if (!form.category?.trim()) return toast.error('Category is required');
    if (!form.uom) return toast.error('Unit of Measure (UoM) is required');
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/material-master/${material.id}`, form);
        toast.success('Material updated');
      } else {
        const res = await api.post('/material-master', form);
        toast.success(`Created · ${res.data.material_id}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto" data-testid="mm-side-panel">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <FileBox size={18} className="text-[#1F7A6E]" />
            {isEdit ? `Edit · ${material.material_id}` : 'Add New Material'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Catalog entry — specs only. Rates &amp; vendors are handled via RFQ / PO modules.
            {isEdit ? '' : ' A unique Material ID will be auto-generated under the chosen HSN bucket.'}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <Section title="Classification" sub="HSN bucket & category drive the unique Material ID.">
            <div className="grid grid-cols-12 gap-2">
              <Field label="HSN Code *" col={12}>
                <div className="relative">
                  <input
                    value={hsnQuery || (form.hsn_code ? `${form.hsn_code}${form.category ? ` — ${form.category}` : ''}` : '')}
                    onChange={(e) => { setHsnQuery(e.target.value); setShowHsnDrop(true); }}
                    onFocus={() => setShowHsnDrop(true)}
                    placeholder="Search HSN code or description (e.g. 7214, cement, paint)…"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                    data-testid="mm-hsn-combobox"
                  />
                  {showHsnDrop && filteredHsn.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-64 overflow-y-auto">
                      {filteredHsn.map((h) => (
                        <button
                          key={`${h.hsn}-${h.category}-${h.description}`}
                          onClick={() => pickHsn(h)}
                          className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b border-slate-100"
                          data-testid={`mm-hsn-opt-${h.hsn}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#1F7A6E] text-xs">{h.hsn}</span>
                            <span className="text-[11px] text-slate-700 font-display font-bold">{h.category}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5 truncate">{h.description}</div>
                        </button>
                      ))}
                      <button
                        onClick={() => { setShowHsnDrop(false); }}
                        className="w-full text-center px-3 py-2 text-[10px] uppercase tracking-widest text-slate-400 hover:bg-slate-50"
                      >
                        Or type a new HSN code manually
                      </button>
                    </div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-1">
                    Type to filter the pre-loaded RMC plant HSNs, or enter your own 4/6/8-digit HSN.
                  </div>
                </div>
              </Field>
              <Field label="Category *" col={6}>
                <input value={form.category || ''} onChange={(e) => set('category', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Cement & Binders" />
              </Field>
              <Field label="Sub-category" col={6}>
                <input value={form.sub_category || ''} onChange={(e) => set('sub_category', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. OPC 53" />
              </Field>
            </div>
          </Section>

          <Section title="Identity">
            <div className="grid grid-cols-12 gap-2">
              <Field label="Material Name *" col={12}>
                <input value={form.name || ''} onChange={(e) => set('name', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Cement OPC 53" data-testid="mm-field-name" />
              </Field>
              <Field label="Brand / Manufacturer" col={6}>
                <input value={form.brand || ''} onChange={(e) => set('brand', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. TATA Tiscon, ACC, UltraTech" />
              </Field>
              <Field label="Make / Model" col={6}>
                <input value={form.make_model || ''} onChange={(e) => set('make_model', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Specification" col={12}>
                <textarea rows={2} value={form.specification || ''} onChange={(e) => set('specification', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" placeholder="Detailed spec — IS code, dimensions, finish, etc." />
              </Field>
              <Field label="Variant (size / dia / colour code)" col={6}>
                <input value={form.variant || ''} onChange={(e) => set('variant', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. 12mm, 6mtr length, RAL-9003" />
              </Field>
              <Field label="Size" col={3}>
                <input value={form.size || ''} onChange={(e) => set('size', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Colour" col={3}>
                <input value={form.color || ''} onChange={(e) => set('color', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Grade" col={6}>
                <input value={form.grade || ''} onChange={(e) => set('grade', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Fe-500D, M25, OPC-53" />
              </Field>
              <Field label="Standard / IS Code" col={6}>
                <input value={form.standard || ''} onChange={(e) => set('standard', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. IS-1786, IS-269" />
              </Field>
            </div>
          </Section>

          <Section title="Units &amp; Storage">
            <div className="grid grid-cols-12 gap-2">
              <Field label="Primary UoM *" col={4}>
                <select value={form.uom || ''} onChange={(e) => set('uom', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="mm-field-uom">
                  <option value="">— Select —</option>
                  {UOM_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Alt UoM" col={4}>
                <select value={form.alt_uom || ''} onChange={(e) => set('alt_uom', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">—</option>
                  {UOM_OPTIONS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="1 Primary = ? Alt" col={4}>
                <input type="number" value={form.uom_conversion ?? ''} onChange={(e) => set('uom_conversion', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. 1000" />
              </Field>
              <Field label="Shelf Life (days)" col={4}>
                <input type="number" value={form.shelf_life_days ?? ''} onChange={(e) => set('shelf_life_days', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Lead Time (days)" col={4}>
                <input type="number" value={form.lead_time_days ?? ''} onChange={(e) => set('lead_time_days', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </Field>
              <Field label="Hazard Class" col={4}>
                <input value={form.hazard_class || ''} onChange={(e) => set('hazard_class', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Flammable, Corrosive" />
              </Field>
              <Field label="Storage Conditions" col={12}>
                <input value={form.storage_conditions || ''} onChange={(e) => set('storage_conditions', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="e.g. Dry covered shed, max 30 days" />
              </Field>
            </div>
          </Section>

          <Section title="Flags &amp; Tags">
            <div className="grid grid-cols-12 gap-2">
              <Field label="Flags" col={12}>
                <div className="flex flex-wrap gap-2">
                  <Checkbox label="Critical (high-impact, monitor closely)" checked={!!form.is_critical} onChange={(v) => set('is_critical', v)} />
                  <Checkbox label="Consumable (PPE, fasteners, gloves)" checked={!!form.is_consumable} onChange={(v) => set('is_consumable', v)} />
                  <Checkbox label="Serialised (track by serial number)" checked={!!form.is_serialized} onChange={(v) => set('is_serialized', v)} />
                </div>
              </Field>
              <Field label="Tags" col={12}>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="Type a tag and press Enter…"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                  <button onClick={addTag} className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-xs font-display font-bold rounded-sm">Add</button>
                </div>
                {form.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.tags.map((t) => (
                      <span key={t} className="bg-teal-50 text-[#1F7A6E] px-2 py-0.5 rounded text-[11px] flex items-center gap-1">
                        {t}
                        <button onClick={() => set('tags', form.tags.filter((x) => x !== t))} className="text-[#1F7A6E]"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
              <Field label="MSDS / Datasheet URL" col={6}>
                <input value={form.msds_url || ''} onChange={(e) => set('msds_url', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="https://…" />
              </Field>
              <Field label="Image URL" col={6}>
                <input value={form.image_url || ''} onChange={(e) => set('image_url', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" placeholder="https://…" />
              </Field>
              <Field label="Remarks" col={12}>
                <textarea rows={2} value={form.remarks || ''} onChange={(e) => set('remarks', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
              </Field>
            </div>
          </Section>

          <div className="sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-display">
              {isEdit
                ? `Editing ${material.material_id}`
                : (form.hsn_code ? `Next ID: MAT-${form.hsn_code}-XXXX` : 'Pick HSN to preview ID')}
            </div>
            <div className="flex gap-2">
              <button onClick={() => onOpenChange(false)} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-100 rounded-sm" data-testid="mm-panel-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-widest rounded-sm flex items-center gap-2 disabled:opacity-50" data-testid="mm-panel-save">
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                {isEdit ? 'Update' : 'Create Material'}
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function blankForm() {
  return {
    name: '', hsn_code: '', category: '', sub_category: '',
    brand: '', make_model: '', specification: '', variant: '',
    size: '', color: '', grade: '', standard: '',
    uom: '', alt_uom: '', uom_conversion: '',
    shelf_life_days: '', storage_conditions: '', lead_time_days: '',
    hazard_class: '', msds_url: '', image_url: '',
    is_consumable: false, is_serialized: false, is_critical: false,
    tags: [], remarks: '',
  };
}

function Section({ title, sub, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-display text-slate-500 mb-1">{title}</div>
      {sub && <div className="text-[10px] text-slate-400 mb-2">{sub}</div>}
      {children}
    </div>
  );
}

function Field({ label, col = 12, children }) {
  const colMap = { 3: 'col-span-3', 4: 'col-span-4', 6: 'col-span-6', 8: 'col-span-8', 12: 'col-span-12' };
  return (
    <div className={colMap[col] || 'col-span-12'}>
      <label className="block text-[10px] uppercase tracking-widest font-display text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-sm border border-slate-200">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#1F7A6E]" />
      {label}
    </label>
  );
}
